import type { TFunction } from 'i18next';
import { runInAction } from 'mobx';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Campaign,
  DisplayCreative,
  FacebookDisplayCreative,
  IDimensionDefinition,
  TChannel,
} from '@feathr/blackbox';
import {
  CreativeClass,
  EIntegrationTypes,
  FacebookVideoCreative,
  getAspectRatioNotation,
  isMetaBlockedDimension,
  ValidFacebookDimensions,
  ValidFacebookVideoDimensions,
} from '@feathr/blackbox';
import type { IUploadedFile } from '@feathr/components';
import { toast } from '@feathr/components';
import type {
  ICreativeFlowBaseParams,
  IUploadResult,
} from '@feathr/extender/components/creatives/creatives.types';
import {
  findClosestValidAspectRatio,
  validateDisplayCreativeDimensions,
  validateMetaCreativeDimensions,
} from '@feathr/extender/components/creatives/dimensionValidation.utils';
import {
  getCreativeThumbnailUrl,
  hasStoredThumbnail,
  isVideoCreative,
} from '@feathr/extender/components/creatives/display.utils';
import {
  getVideoDimensions,
  sanitizeFilename,
} from '@feathr/extender/components/creatives/fileProcessing.utils';
import {
  createTransformedImage,
  extractAndUploadVideoThumbnail,
  getFilestackClient,
} from '@feathr/extender/components/creatives/filestack.utils';
import { getMetaPortraitUploadError } from '@feathr/extender/components/creatives/status.utils';
import { useStore } from '@feathr/extender/state';
import { useAccount } from '@feathr/report_components';

import type { IThumbnailCarouselItem } from '../CreativeModal/ThumbnailCarousel';

/** Steps in the Add Image/Video flow */
export type TAddImageVideoStep = 'upload' | 'creativeModal';

/** Upload state machine states */
type TUploadState = 'idle' | 'uploading' | 'processing';

export interface IUseAddImageVideoFlowParams extends ICreativeFlowBaseParams {
  /** The channel type (meta or display) */
  channel: TChannel;
  /** Campaign the creatives are being added to; scopes dimension validation to the
   * sizes valid for this campaign class (e.g. MobileGeoFencing → the mobile-geo
   * allowlist). */
  campaign?: Campaign;
}

export interface IUseAddImageVideoFlowReturn {
  /** Current step in the flow */
  step: TAddImageVideoStep;
  /** Current upload state (idle, uploading, or processing) */
  uploadState: TUploadState;
  /** Whether creatives are currently being saved */
  isSaving: boolean;
  /** Array of created creatives */
  creatives: DisplayCreative[];
  /** Currently selected creative for the modal */
  selectedCreative: DisplayCreative | undefined;
  /** Thumbnail items for the carousel */
  thumbnailItems: IThumbnailCarouselItem[];
  /** Results of the upload process (successes and failures) */
  uploadResults: IUploadResult[];
  /** Handle upload state changes from FilePicker */
  handleUploadingChange: (isUploading: boolean) => void;
  /** Handle files uploaded from FilePicker */
  handleFilesUploaded: (files: IUploadedFile[]) => Promise<void>;
  /** Handle selecting a creative in the carousel */
  handleSelectCreative: (id: string) => void;
  /** Handle continuing to the creative modal after reviewing upload results */
  handleContinue: () => void;
  /** Handle closing the creative modal (saves creatives to backend) */
  handleSave: () => Promise<void>;
  /** Handle back button click */
  handleBack: () => void;
  /** Handle close (called after save or cancel) */
  handleClose: () => void;
}

/** Result of processing a single file */
interface IProcessFileResult {
  creative: DisplayCreative | null;
  result: IUploadResult;
}

/** Normalized file dimensions with optional duration for videos */
interface INormalizedDimensions {
  width: number;
  height: number;
  duration: number;
}

/** Media type flags for a file */
interface IMediaType {
  isVideo: boolean;
  isGif: boolean;
}

/**
 * Extracts media type flags from a mimetype.
 */
function getMediaType(mimetype: string): IMediaType {
  return {
    isVideo: mimetype.includes('video'),
    isGif: mimetype.includes('gif'),
  };
}

/**
 * Normalizes file dimensions, fetching accurate video dimensions when possible.
 *
 * @param file - The uploaded file metadata
 * @returns Normalized dimensions or null if dimensions couldn't be determined
 */
async function normalizeFileDimensions(
  file: IUploadedFile,
  isVideo: boolean,
): Promise<INormalizedDimensions | null> {
  let { width, height } = file;
  let duration = 0;

  // For videos, get accurate dimensions from video element
  if (isVideo && width && height) {
    try {
      const videoDimensions = await getVideoDimensions(file.url);
      width = videoDimensions.width;
      height = videoDimensions.height;
      duration = videoDimensions.duration;
    } catch {
      // Use dimensions from file metadata if video analysis fails
    }
  }

  if (!width || !height) {
    return null;
  }

  return { width, height, duration };
}

/**
 * Builds the error message for invalid Meta dimensions.
 */
function buildMetaDimensionError(
  width: number,
  height: number,
  validation: {
    isAspectRatioValid: boolean;
    meetsMinDimensions: boolean;
    minWidth?: number;
    minHeight?: number;
  },
  validDimensions: Record<string, IDimensionDefinition>,
  t: TFunction,
): string {
  const formattedRatio = getAspectRatioNotation(width / height);

  if (validation.isAspectRatioValid && !validation.meetsMinDimensions) {
    return t(
      'Too small. For {{ratio}} aspect ratio, minimum dimensions are {{minWidth}}x{{minHeight}}.',
      {
        ratio: formattedRatio,
        minWidth: validation.minWidth,
        minHeight: validation.minHeight,
      },
    );
  }

  const closestRatio = findClosestValidAspectRatio(width, height, validDimensions);
  return t(
    'Unsupported aspect ratio ({{ratio}}). The closest valid aspect ratio would be {{closestRatio}}.',
    { ratio: formattedRatio, closestRatio },
  );
}

/**
 * Builds the error message for invalid Display dimensions.
 */
function buildDisplayDimensionError(
  width: number,
  height: number,
  closestDimension: { width: number; height: number; name: string } | undefined,
  t: TFunction,
): string {
  if (closestDimension) {
    return t(
      'Unsupported aspect ratio ({{actualWidth}}x{{actualHeight}}). Closest valid size: {{closestWidth}}x{{closestHeight}} ({{closestName}}).',
      {
        actualWidth: width,
        actualHeight: height,
        closestWidth: closestDimension.width,
        closestHeight: closestDimension.height,
        closestName: closestDimension.name,
      },
    );
  }
  return t('Unsupported dimensions ({{width}}x{{height}}).', { width, height });
}

/**
 * Uploads a video to Facebook's ad video library.
 *
 * @param creative - The video creative to upload
 * @param fbAccountId - The Facebook account ID
 * @param accessToken - The Facebook access token
 * @param t - Translation function
 * @returns Whether the upload was successful
 */
async function uploadVideoToFacebook(
  creative: FacebookVideoCreative,
  fbAccountId: string | undefined,
  accessToken: string | undefined,
  t: TFunction,
): Promise<boolean> {
  if (!fbAccountId || !accessToken) {
    toast(t('Failed to upload video to Meta: missing account credentials.'), { type: 'error' });
    return false;
  }

  try {
    const body = new FormData();
    body.append('access_token', accessToken);
    body.append('file_url', creative.get('url'));

    const response = await fetch(
      `https://graph.facebook.com/${FACEBOOK_API_VERSION}/${fbAccountId}/advideos`,
      { body, method: 'POST' },
    );

    const json = await response.json();

    if (!response.ok) {
      const errorMessage = json.error?.message || t('Unknown error');
      toast(t('Failed to upload video to Meta: {{errorMessage}}', { errorMessage }), {
        type: 'error',
      });
      return false;
    }

    const videoId = json.id;
    if (!videoId) {
      toast(t('Failed to upload video to Meta: no video ID returned.'), { type: 'error' });
      return false;
    }

    creative.set({ ad_video_id: videoId });
    toast(t('Video uploaded to Meta.'), { type: 'success' });
    return true;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : t('Unknown error');
    toast(t('Failed to upload video to Meta: {{errorMessage}}', { errorMessage }), {
      type: 'error',
    });
    return false;
  }
}

interface IProcessFileParams {
  file: IUploadedFile;
  campaignId?: string;
  eventId?: string;
  campaign?: Campaign;
  accountId: string;
  createCreative: (attrs: Record<string, unknown>) => DisplayCreative;
  t: TFunction;
}

/**
 * Processes an uploaded file for Meta channel.
 */
async function processMetaFile(params: IProcessFileParams): Promise<IProcessFileResult> {
  const { file, campaignId, eventId, accountId, createCreative, t } = params;
  const { url, mimetype, filename } = file;
  const { isVideo, isGif } = getMediaType(mimetype);

  const dimensions = await normalizeFileDimensions(file, isVideo);
  if (!dimensions) {
    return {
      creative: null,
      result: {
        filename,
        success: false,
        error: t('Could not be processed - dimensions unknown.'),
        url,
      },
    };
  }

  const { width, height, duration } = dimensions;

  if (isMetaBlockedDimension(width, height)) {
    return {
      creative: null,
      result: {
        filename,
        success: false,
        error: getMetaPortraitUploadError(t),
        url,
      },
    };
  }

  const cls = isVideo || isGif ? CreativeClass.FacebookVideo : CreativeClass.FacebookImage;
  const validDimensions: Record<string, IDimensionDefinition> =
    isVideo || isGif ? ValidFacebookVideoDimensions : ValidFacebookDimensions;

  const validation = validateMetaCreativeDimensions(width, height, validDimensions);

  if (!validation.isValid) {
    return {
      creative: null,
      result: {
        filename,
        success: false,
        error: buildMetaDimensionError(width, height, validation, validDimensions, t),
        url,
      },
    };
  }

  const creative = createCreative({
    width,
    height,
    duration,
    mimetype,
    url,
    _cls: cls,
    ...(campaignId ? { parent: campaignId } : {}),
    ...(eventId && !campaignId ? { event: eventId } : {}),
    name: `${sanitizeFilename(filename)} (${width}x${height})`,
    is_enabled: true,
    account: accountId,
    integrations: [EIntegrationTypes.Meta],
  }) as FacebookDisplayCreative;

  if (validation.matchingSpec) {
    creative.set({ spec: validation.matchingSpec });
  }

  return {
    creative,
    result: { filename, success: true, url },
  };
}

/**
 * Processes an uploaded file for Display channel.
 */
async function processDisplayFile(params: IProcessFileParams): Promise<IProcessFileResult> {
  const { file, campaignId, eventId, campaign, accountId, createCreative, t } = params;
  const { url, mimetype, filename, handle } = file;
  const { isVideo, isGif } = getMediaType(mimetype);

  const dimensions = await normalizeFileDimensions(file, isVideo);
  if (!dimensions) {
    return {
      creative: null,
      result: {
        filename,
        success: false,
        error: t('Could not be processed - dimensions unknown.'),
        url,
      },
    };
  }

  const { width, height, duration } = dimensions;
  const validation = validateDisplayCreativeDimensions(width, height, isVideo, campaign);

  if (!validation.isValid) {
    return {
      creative: null,
      result: {
        filename,
        success: false,
        error: buildDisplayDimensionError(width, height, validation.closestDimension, t),
        url,
      },
    };
  }

  // GIFs can't be resized, so reject them if they would need resizing
  if (isGif && validation.targetDimension) {
    return {
      creative: null,
      result: {
        filename,
        success: false,
        error: buildDisplayDimensionError(width, height, validation.targetDimension, t),
        url,
      },
    };
  }

  // Determine final dimensions and URL
  let finalUrl = url;
  let finalWidth = width;
  let finalHeight = height;

  // If we have a target dimension (needs resizing) and it's an image (not video/gif)
  if (validation.targetDimension && !isVideo && !isGif) {
    try {
      const client = await getFilestackClient();
      finalUrl = await createTransformedImage(client, handle, validation.targetDimension);
      finalWidth = validation.targetDimension.width;
      finalHeight = validation.targetDimension.height;
    } catch {
      return {
        creative: null,
        result: {
          filename,
          success: false,
          error: t('Failed to resize image to {{width}}x{{height}}.', {
            width: validation.targetDimension.width,
            height: validation.targetDimension.height,
          }),
          url,
        },
      };
    }
  }

  const cls = isVideo ? CreativeClass.DisplayVideo : CreativeClass.DisplayImage;
  const creative = createCreative({
    width: finalWidth,
    height: finalHeight,
    duration,
    mimetype,
    url: finalUrl,
    _cls: cls,
    ...(campaignId ? { parent: campaignId } : {}),
    ...(eventId && !campaignId ? { event: eventId } : {}),
    name: `${sanitizeFilename(filename)} (${finalWidth}x${finalHeight})`,
    is_enabled: true,
    account: accountId,
    spec: validation.matchingSpec,
  });

  return {
    creative,
    result: { filename, success: true, url: finalUrl },
  };
}

/**
 * Unified hook for managing the Add Image/Video flow state for both Meta and Display channels.
 *
 * @param params - Configuration parameters including channel type
 * @returns Flow state and handlers
 */
export function useAddImageVideoFlow(
  params: IUseAddImageVideoFlowParams,
): IUseAddImageVideoFlowReturn {
  const {
    channel,
    campaignId,
    eventId,
    campaign,
    creatives: creativesArray,
    onAppear,
    onComplete,
    onBack,
    onCancel,
  } = params;
  const { t } = useTranslation();
  const { Creatives } = useStore();
  const account = useAccount();
  const integration = account.get('facebook');

  const [step, setStep] = useState<TAddImageVideoStep>('upload');
  const [uploadState, setUploadState] = useState<TUploadState>('idle');
  const [isSaving, setIsSaving] = useState(false);
  const [creatives, setCreatives] = useState<DisplayCreative[]>([]);
  const [selectedCreativeId, setSelectedCreativeId] = useState<string | undefined>();
  const [uploadResults, setUploadResults] = useState<IUploadResult[]>([]);

  // Ref to prevent race conditions during file processing
  const isProcessingRef = useRef(false);
  // Queue for files that arrive while processing is in progress
  const pendingFilesRef = useRef<IUploadedFile[]>([]);
  // Track which creatives have been saved (persists across handleSave calls)
  const savedCreativeIdsRef = useRef(new Set<string>());

  const selectedCreative = creatives.find((c) => c.id === selectedCreativeId);

  const thumbnailItems = useMemo<IThumbnailCarouselItem[]>(
    () =>
      creatives.map((creative) => ({
        id: creative.id,
        thumbnailUrl: getCreativeThumbnailUrl(creative),
        filename: creative.get('name') ?? '',
        isVideo: isVideoCreative(creative),
        hasThumbnail: hasStoredThumbnail(creative),
      })),
    [creatives],
  );

  const createCreative = useCallback(
    (attrs: Record<string, unknown>) => Creatives.create(attrs) as DisplayCreative,
    [Creatives],
  );

  const processUploadedFile = useCallback(
    async (file: IUploadedFile): Promise<IProcessFileResult> => {
      const baseParams: IProcessFileParams = {
        file,
        campaignId,
        eventId,
        campaign,
        accountId: account.id,
        createCreative,
        t,
      };
      if (channel === 'meta') {
        return processMetaFile(baseParams);
      }
      return processDisplayFile(baseParams);
    },
    [account.id, campaign, campaignId, eventId, channel, createCreative, t],
  );

  /**
   * Processes a batch of files, updating state with results.
   */
  const processBatch = useCallback(
    async (files: IUploadedFile[]): Promise<void> => {
      const newCreatives: DisplayCreative[] = [];
      const newResults: IUploadResult[] = [];

      for (const file of files) {
        const { creative, result } = await processUploadedFile(file);
        newResults.push(result);
        if (creative) {
          newCreatives.push(creative);
        }
      }

      // Append new results to existing ones
      setUploadResults((prev) => [...prev, ...newResults]);

      // Append new creatives to existing ones
      setCreatives((prev) => [...prev, ...newCreatives]);

      // Set selected creative to first new one if none selected yet
      setSelectedCreativeId((prev) => {
        if (!prev && newCreatives.length > 0) {
          return newCreatives[0].id;
        }
        return prev;
      });
    },
    [processUploadedFile],
  );

  const handleUploadingChange = useCallback((uploading: boolean): void => {
    if (uploading) {
      setUploadState('uploading');
    }
    // Ignore false - we transition to 'processing' or 'idle' in handleFilesUploaded
  }, []);

  const handleFilesUploaded = useCallback(
    async (files: IUploadedFile[]): Promise<void> => {
      // Ignore empty arrays - Filestack may call onUploadDone at unexpected times
      if (files.length === 0) {
        setUploadState('idle');
        return;
      }

      // If already processing, queue files for later
      if (isProcessingRef.current) {
        pendingFilesRef.current.push(...files);
        return;
      }

      isProcessingRef.current = true;
      setUploadState('processing');

      try {
        // Process incoming files
        await processBatch(files);

        // Process any files that arrived while we were processing
        while (pendingFilesRef.current.length > 0) {
          const pendingFiles = [...pendingFilesRef.current];
          pendingFilesRef.current = [];
          await processBatch(pendingFiles);
        }
      } catch {
        toast(t('An unexpected error occurred while processing files.'), { type: 'error' });
      } finally {
        setUploadState('idle');
        isProcessingRef.current = false;
      }
    },
    [processBatch, t],
  );

  const handleContinue = useCallback((): void => {
    if (creatives.length > 0) {
      setStep('creativeModal');
    }
  }, [creatives.length]);

  const handleSelectCreative = useCallback((id: string): void => {
    setSelectedCreativeId(id);
  }, []);

  const handleSave = useCallback(async (): Promise<void> => {
    setIsSaving(true);
    let hasErrors = false;

    // Save all creatives to the backend and add to observable array
    // Process in reverse order so that the first creative ends up at the top of the list
    for (const creative of [...creatives].reverse()) {
      // Skip if already saved (in case of partial retry)
      if (savedCreativeIdsRef.current.has(creative.id)) {
        continue;
      }

      try {
        // For Meta videos, upload to Facebook before saving to backend
        if (channel === 'meta' && creative instanceof FacebookVideoCreative) {
          const uploadSuccess = await uploadVideoToFacebook(
            creative,
            integration?.id,
            account?.getFacebookAccessToken,
            t,
          );
          if (!uploadSuccess) {
            hasErrors = true;
            continue;
          }

          // Extract and upload a video thumbnail for Meta submission
          try {
            const thumbnailUrl = await extractAndUploadVideoThumbnail(creative.get('url'));
            creative.set({ ad_video_thumbnail: thumbnailUrl });
          } catch {
            toast(t('Could not generate video thumbnail.'), { type: 'warning' });
          }
        }

        // Don't refresh API cache - we're manually managing the list order
        const savedCreative = (await Creatives.add(creative, {
          refreshApiCache: false,
        })) as DisplayCreative;

        savedCreativeIdsRef.current.add(creative.id);

        if (creativesArray) {
          runInAction(() => {
            creativesArray.unshift(savedCreative);
          });
          onAppear?.(savedCreative.id);
        }
      } catch {
        hasErrors = true;
        toast(t('Failed to save creative "{{name}}"', { name: creative.get('name') }), {
          type: 'error',
        });
      }
    }

    setIsSaving(false);

    // Remove successfully saved creatives from local state
    // so if user retries, we don't duplicate
    if (savedCreativeIdsRef.current.size > 0) {
      setCreatives((prev) => prev.filter((c) => !savedCreativeIdsRef.current.has(c.id)));
    }

    // Only clear tracking if all saves succeeded
    if (!hasErrors) {
      savedCreativeIdsRef.current.clear();
    }
  }, [Creatives, account, channel, creatives, creativesArray, integration?.id, onAppear, t]);

  /**
   * Unified close handler. Determines whether this is a save or cancel based on
   * whether ephemeral creatives remain (cancel) or all were saved (complete).
   */
  const handleClose = useCallback((): void => {
    // Check if any ephemeral creatives remain (indicates cancel, not save)
    const hasEphemeral = creatives.some((c) => c.isEphemeral);

    if (hasEphemeral) {
      // Cancel path: remove ephemeral creatives from store
      for (const creative of creatives) {
        if (creative.isEphemeral) {
          Creatives.removeEphemeral(creative.id);
        }
      }
      onCancel();
    } else {
      // Save path: all creatives were saved successfully
      onComplete();
    }
  }, [Creatives, creatives, onCancel, onComplete]);

  return {
    step,
    uploadState,
    isSaving,
    creatives,
    selectedCreative,
    thumbnailItems,
    uploadResults,
    handleUploadingChange,
    handleFilesUploaded,
    handleSelectCreative,
    handleContinue,
    handleSave,
    handleBack: onBack,
    handleClose,
  };
}
